/**
 * Which track endpoint to ask, and with what (#801).
 *
 * Three cases, kept apart deliberately (each by registration when one is
 * given, #866, else by the tracker's ESN):
 *  - no span: the long-standing 48h route the live layer has always used, so
 *    toggling the AFF layer behaves exactly as before;
 *  - a fixed window: the parameterised sibling added for the explorer (#779);
 *  - an explicit range: the custom start/end the Aviation group asked for,
 *    because a window measured back from now cannot draw a particular shift.
 *
 * Extracted from AffLayerProvider so the URL can be asserted directly rather
 * than inferred from a mocked fetch.
 */
import { nwtLocalDateTimeToIso } from '../../../utils/nwtDate';

export interface TrackSpan {
  hours?: number;
  start?: string;
  end?: string;
  /**
   * #866 — draw the aircraft rather than the tracker: every ESN that has
   * reported under this registration over the span. Only honoured with an
   * hours or start/end span; the ESN route stays the fallback when absent.
   */
  registration?: string;
}

/** A picked NWT wall-clock time as an instant; an unreadable one is an error, as in the grid. */
const nwtInstant = (value: string): string => {
  const iso = nwtLocalDateTimeToIso(value);
  if (!iso) throw new Error(`AFF track: ${value} is not a date and time`);
  return iso;
};

export const buildTrackUrl = (backendUrl: string, esn: string, span: TrackSpan): string => {
  // Used only with a span below; the no-span 48h route stays per ESN.
  const base = span.registration
    ? `${backendUrl}/api/aff/track/by-registration/${encodeURIComponent(span.registration)}`
    : `${backendUrl}/api/aff/track/${encodeURIComponent(esn)}`;

  // A range is the more specific request; the router refuses a half-specified
  // one, so only a complete pair is sent as a range.
  if (span.start && span.end) {
    // The pickers give wall-clock time with no offset. It means NWT time
    // whatever zone the browser is in (#866 U6), so it is converted as NWT
    // and sent as an instant; the server would otherwise read it as UTC.
    const query = new URLSearchParams({ start: nwtInstant(span.start), end: nwtInstant(span.end) });
    return `${base}?${query.toString()}`;
  }

  if (span.hours) {
    return `${base}?hours=${span.hours}`;
  }

  return `${backendUrl}/api/aff/trackAc48h/${encodeURIComponent(esn)}`;
};
